'use client'

import { ArrowRightIcon } from '@repo/ui/ArrowRightIcon'
import { InfoIcon } from '@repo/ui/InfoIcon'
import { OspreyFieldSelect } from '@repo/ui/OspreyFieldSelect'
import { Pill } from '@repo/ui/Pill'
import MUIAlert from '@mui/material/Alert'
import MUIBox from '@mui/material/Box'
import MUIIconButton from '@mui/material/IconButton'
import MUIStack from '@mui/material/Stack'
import MUITextField from '@mui/material/TextField'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import { FormField, getUnsaveableReason, isFieldSelectValueComplete, OspreyFieldSelectValue } from './mapFieldsStepHelpers'

type Props = {
  formField: FormField
  value: OspreyFieldSelectValue | null
  onChange: (value: OspreyFieldSelectValue | null) => void
  workflowTypeId?: string
  isReadOnlyFormula?: boolean
}

export const MapFieldsRow = ({ formField, value, onChange, workflowTypeId, isReadOnlyFormula = false }: Props) => {
  const isComplete = isFieldSelectValueComplete(value)
  const unsaveableReason = getUnsaveableReason(value)
  const isFieldReadOnly = Boolean(formField.isReadOnly || formField.isAutoCompleted)

  return (
    <MUIStack gap={1} data-testid={`MapFieldsRow-${formField.formFieldId}`}>
      <MUIStack
        data-testid="MapFieldsRow-cells"
        direction="row"
        gap={2}
        alignItems="stretch"
        sx={{
          p: 1.5,
          borderRadius: '4px',
          backgroundColor: isComplete ? 'rgba(31, 137, 71, 0.08)' : 'transparent'
        }}
      >
        <MUIBox data-testid="MapFieldsRow-left" sx={{ flex: 1, minWidth: 0 }}>
          {isReadOnlyFormula ? (
            <MUITextField
              data-testid="MapFieldsRowReadOnlyFormula"
              label="Formula"
              value=""
              placeholder="Existing formula mapping (not editable here)"
              disabled
              fullWidth
              multiline
              rows={3}
            />
          ) : (
            <OspreyFieldSelect value={value} onChange={onChange} workflowTypeId={workflowTypeId} />
          )}
        </MUIBox>

        <MUIBox sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20px' }}>
          {isComplete && <ArrowRightIcon sx={{ width: '20px', height: '20px', color: '#1F8947' }} />}
        </MUIBox>

        <MUIBox data-testid="MapFieldsRow-right" sx={{ flex: 1, minWidth: 0 }}>
          <MUIStack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <MUIStack sx={{ minWidth: 0 }}>
              <MUITypography sx={{ fontWeight: 700, wordBreak: 'break-word', mb: 0 }}>
                {formField.name}
              </MUITypography>
              <MUITypography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word', mb: 0 }}>
                {formField.description || '—'}
              </MUITypography>
            </MUIStack>

            <MUIStack direction="row" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
              {isFieldReadOnly && <Pill label="READ-ONLY" backgroundColor="#ECEEF2" color="#051C33" />}

              <MUITooltip
                arrow
                placement="top"
                title={`Field ID: ${formField.formFieldId} · Field type: ${formField.fieldType}`}
              >
                <MUIIconButton size="small" aria-label={`Field details for ${formField.name}`}>
                  <InfoIcon sx={{ width: '16px', height: '16px', color: '#3D517B8F' }} />
                </MUIIconButton>
              </MUITooltip>
            </MUIStack>
          </MUIStack>
        </MUIBox>
      </MUIStack>

      {unsaveableReason && (
        <MUIAlert severity="warning" data-testid="MapFieldsRow-unsaveable">
          {unsaveableReason}
        </MUIAlert>
      )}
    </MUIStack>
  )
}
